import { useEffect, useMemo, useState } from "react";
import type { ImportDocumentSnapshot } from "@magic-md/core";
import {
    type AssetReferenceEntry,
    collectDocumentAssetReferences,
    loadDocumentAssetReferences,
} from "../lib/asset-reference-list";
import { useEditorStore } from "../store/editor-store";

export function useDocumentAssetReferences(
    documentPath: string | null | undefined,
    importDocuments?: Map<string, ImportDocumentSnapshot>,
): AssetReferenceEntry[] {
    const fileTree = useEditorStore((state) => state.fileTree);
    const importSources = useMemo(
        () => Array.from(importDocuments?.entries() ?? [])
            .map(([namespace, snapshot]) => ({
                namespace,
                documentPath: snapshot.resolvedPath,
            }))
            .filter((entry): entry is { namespace: string; documentPath: string } =>
                Boolean(entry.documentPath),
            ),
        [importDocuments],
    );

    const treeEntries = useMemo(
        () => [
            ...collectDocumentAssetReferences(fileTree, documentPath),
            ...importSources.flatMap((entry) =>
                collectDocumentAssetReferences(
                    fileTree,
                    entry.documentPath,
                    entry.namespace,
                ),
            ),
        ],
        [fileTree, documentPath, importSources],
    );
    const [loadedEntries, setLoadedEntries] = useState<AssetReferenceEntry[]>([]);

    useEffect(() => {
        let cancelled = false;
        setLoadedEntries([]);
        const sources = [
            { documentPath, namespace: undefined },
            ...importSources,
        ].filter((entry): entry is { documentPath: string; namespace?: string } =>
            Boolean(entry.documentPath),
        );
        if (sources.length === 0) return;

        void Promise.all(
            sources.map((entry) =>
                loadDocumentAssetReferences(entry.documentPath, entry.namespace),
            ),
        ).then((entryGroups) => {
            if (!cancelled) setLoadedEntries(entryGroups.flat());
        });

        return () => {
            cancelled = true;
        };
    }, [documentPath, importSources]);

    return mergeAssetEntries(treeEntries, loadedEntries);
}

function mergeAssetEntries(
    primary: AssetReferenceEntry[],
    secondary: AssetReferenceEntry[],
): AssetReferenceEntry[] {
    const entries = new Map<string, AssetReferenceEntry>();
    for (const entry of [...secondary, ...primary]) {
        entries.set(`${entry.namespace ?? ""}:${entry.relativePath}`, entry);
    }
    return Array.from(entries.values()).sort((a, b) => {
        const ns = (a.namespace ?? "").localeCompare(b.namespace ?? "");
        if (ns !== 0) return ns;
        if (a.isDirectory !== b.isDirectory) return a.isDirectory ? -1 : 1;
        return a.relativePath.localeCompare(b.relativePath);
    });
}
